import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Spin, message, Table, Tag, Progress } from 'antd';
import { DollarOutlined, TeamOutlined, ClockCircleOutlined, RiseOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const DashboardRH = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [mesAno, setMesAno] = useState(dayjs());

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    carregarDados();
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year()
      };

      const response = await dashboardService.getRH(params);
      setDados(response.data);
    } catch (error) {
      message.error('Erro ao carregar dados do dashboard RH');
      console.error(error);
    }
    setLoading(false);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (loading || !dados) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis, custo_por_professor, evolucao_folha, distribuicao_funcao } = dados;

  const colunasCustoProfessor = [
    {
      title: 'Professor',
      dataIndex: 'professor',
      key: 'professor',
      width: 200,
    },
    {
      title: 'Função',
      dataIndex: 'funcao',
      key: 'funcao',
      width: 120,
      render: (funcao) => <Tag color="blue">{funcao}</Tag>,
    },
    {
      title: 'Horas',
      dataIndex: 'horas',
      key: 'horas',
      align: 'center',
      width: 100,
      render: (horas) => `${horas}h`,
    },
    {
      title: 'Custo',
      dataIndex: 'custo',
      key: 'custo',
      align: 'right',
      width: 120,
      render: (valor) => formatarMoeda(valor),
    },
    {
      title: 'Custo/Hora',
      dataIndex: 'custo_hora',
      key: 'custo_hora',
      align: 'right',
      width: 120,
      render: (valor) => formatarMoeda(valor),
    },
  ];

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      {/* Cabeçalho */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard RH</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gestão de equipe e pagamentos</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
          />
        </Col>
      </Row>

      {/* KPIs Principais */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Custo Total da Folha"
              value={kpis.custo_folha.total}
              precision={2}
              prefix="R$"
              suffix={
                <span style={{ fontSize: '14px', color: kpis.custo_folha.variacao <= 0 ? '#3f8600' : '#faad14' }}>
                  {kpis.custo_folha.variacao >= 0 ? '↑' : '↓'} {Math.abs(kpis.custo_folha.variacao).toFixed(1)}%
                </span>
              }
              valueStyle={{ color: '#ff4d4f' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              vs mês anterior
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Equipe Ativa"
              value={kpis.equipe.total}
              prefix={<TeamOutlined />}
              suffix={
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  ({kpis.equipe.ativos_mes} trabalharam)
                </span>
              }
              valueStyle={{ color: '#1890ff' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Membros cadastrados
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Horas Trabalhadas"
              value={kpis.horas_trabalhadas}
              precision={1}
              suffix="h"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Custo/hora: {formatarMoeda(kpis.produtividade.custo_hora)}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Margem por Hora"
              value={kpis.produtividade.margem_hora}
              precision={2}
              prefix="R$"
              valueStyle={{ color: kpis.produtividade.margem_hora >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Receita: {formatarMoeda(kpis.produtividade.receita_hora)}/h
            </p>
          </Card>
        </Col>
      </Row>

      {/* Status da Folha */}
      <Card title="Status dos Pagamentos" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Pago</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#52c41a' }}>
                {formatarMoeda(kpis.custo_folha.pago)}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Pendente</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#faad14' }}>
                {formatarMoeda(kpis.custo_folha.pendente)}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Taxa de Pagamento</div>
              <Progress
                type="circle"
                percent={kpis.custo_folha.taxa_pagamento}
                format={percent => `${percent.toFixed(0)}%`}
                strokeColor={kpis.custo_folha.taxa_pagamento >= 80 ? '#52c41a' : '#faad14'}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Evolução dos Pagamentos (6 meses)">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={evolucao_folha}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value, name) => {
                  if (name === 'Custo') return formatarMoeda(value);
                  return `${value}h`;
                }} />
                <Legend />
                <Bar yAxisId="left" dataKey="custo" fill="#ff4d4f" name="Custo" />
                <Line yAxisId="right" type="monotone" dataKey="horas" stroke="#1890ff" strokeWidth={2} name="Horas" />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Distribuição por Função">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distribuicao_funcao}
                  dataKey="quantidade"
                  nameKey="funcao"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.funcao}: ${entry.quantidade}`}
                >
                  {distribuicao_funcao.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Tabela de Custos */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Top 10 Professores por Custo" extra={<TeamOutlined />}>
            <Table
              dataSource={custo_por_professor}
              columns={colunasCustoProfessor}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
              scroll={{ x: 700 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardRH;
